// Detail sheets opened over the current screen (annonce, achat, objet, vente).
// Any reference to a record opens its sheet here, without leaving the page:
// "← Retour" goes back to the previous sheet, × back to the screen as it was.
// The package editor is a page: going there saves the open sheets, restored
// when the browser goes back (see navigateFromDetail and DetailHost).
import { reactive } from "vue";
import router from "./router";

export const DETAIL_TYPES = ["offer", "purchase", "item", "sale"];
export const DETAIL_LABELS = {
  offer: "l’annonce",
  purchase: "l’achat",
  item: "l’objet",
  sale: "la vente",
};

export const detailStack = reactive({
  entries: [], // { type, id, from, fromId }: the sheet it was opened from
});

// from / fromId: the sheet of the page the link was clicked in, for "Retour"
export const openDetail = (type, id, { from = null, fromId = null } = {}) => {
  if (!DETAIL_TYPES.includes(type) || !id) return;
  id = String(id);
  // The sheet of the page these sheets were opened from: back to it
  const first = detailStack.entries[0];
  if (first && first.from === type && first.fromId === id) {
    closeDetails();
    return;
  }
  // Already open lower in the stack: back to it rather than a loop
  const index = detailStack.entries.findIndex(
    (entry) => entry.type === type && entry.id === id,
  );
  if (index >= 0) {
    detailStack.entries.splice(index + 1);
    return;
  }
  const previous = detailStack.entries[detailStack.entries.length - 1];
  detailStack.entries.push({
    type,
    id,
    from: previous ? previous.type : from,
    fromId: previous ? previous.id : fromId && String(fromId),
  });
};

export const backDetail = () => {
  detailStack.entries.pop();
};

export const closeDetails = () => {
  detailStack.entries.splice(0);
};

// Direct link of a sheet, from any page: <page>?open=item:123
export const detailLink = (type, id, path = window.location.pathname) =>
  `${path}?open=${type}:${encodeURIComponent(id)}`;

export const parseOpenParam = (value) =>
  String(value || "")
    .split(",")
    .map((part) => part.split(":"))
    .filter(([type, id]) => DETAIL_TYPES.includes(type) && id)
    .map(([type, id]) => ({ type, id: decodeURIComponent(id) }));

// Sheets to restore when the browser comes back to a history entry
const snapshots = new Map();
const historyPosition = () => window.history.state?.position;

// Leaves the page for another one (package editor…) from a sheet. pagePanel:
// the sheet open in the page itself ({ type, id }), restored over the page on
// return when the page does not keep its state (not kept alive).
export const navigateFromDetail = (to, { pagePanel = null } = {}) => {
  const target = router.resolve(to);
  const current = router.currentRoute.value;
  if (target.fullPath === current.fullPath) {
    closeDetails(); // already there (the package being edited)
    return;
  }
  if (target.path === current.path) {
    // Same page, an action asked by its address (?offerId=…&edit=1): the
    // page reads it, nothing to restore on return
    closeDetails();
    router.push(target.fullPath);
    return;
  }
  const stack = [...(pagePanel ? [pagePanel] : []), ...detailStack.entries];
  if (stack.length) snapshots.set(historyPosition(), stack);
  closeDetails();
  router.push(to);
};

export const takeSnapshot = () => {
  const position = historyPosition();
  const stack = snapshots.get(position);
  snapshots.delete(position);
  return stack || null;
};

// A sheet changed a record: the lists showing it update
// (record: the saved record when known, to update it in place)
export const notifyChanged = (type, record = null) =>
  window.dispatchEvent(
    new CustomEvent("prexens:changed", { detail: { type, record } }),
  );
